import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from '@shared/components/View';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { MBChatInputUtil } from '@shared/scenes/chat/input/util/index';
import { API } from '@shared/types/api';
import { MBAnalytics } from '@shared/util/analytics';
import { MBChatInputBar } from '../containers/bar';
import { MBChatInputGIFModal } from '../containers/gifModal';
import { MBUrgentBanner } from '../containers/urgentBanner';
import { ChatInputBarProps } from './bar';
import { MentionOption, MentionsMenu } from './mention/MentionsMenu';

const MENTION_ITEM_LIMIT = 50;

export interface ChatInputProps {
  draftId: string;
  initialValue?: string;
  autoFocus?: boolean;
  isEditing?: boolean;
  placeholder?: ChatInputBarProps['placeholder'];
  hideUrgent?: ChatInputBarProps['hideUrgent'];
  users: API.ShortUserFragment[];
  groups: API.ChannelGroupFragment[];
  onSend: (message: string) => void;
  onCancelEdit: () => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onUnmount?(message: string): void;
  onChangeText?: (message: string) => void;
  paddingHorizontal?: number;
}

export function ChatInputComponent({
  draftId,
  initialValue,
  autoFocus,
  isEditing,
  placeholder,
  hideUrgent,
  users,
  groups,
  onSend,
  onCancelEdit,
  onBlur,
  onFocus,
  onUnmount,
  onChangeText,
  paddingHorizontal = 2,
}: ChatInputProps) {
  const [value, setValue] = useState(initialValue || '');
  const [cursorPosition, setCursorPosition] = useState(initialValue ? initialValue.length : 0);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const inputBar = useRef<MBAutosizeTextInput>(null);

  // Keep a ref to the latest value so the unmount cleanup captures it correctly
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    return () => {
      onUnmount?.(valueRef.current);
    };
  }, [onUnmount]);

  const { mentionOptions, hasMoreMentions } = useMemo(() => {
    const query = MBChatInputUtil.mentionQuery(value, cursorPosition);
    if (isNil(query)) {
      return { mentionOptions: [], hasMoreMentions: false };
    }
    const filteredGroups = isEmpty(query) ? groups : groups.filter(g => g.id.includes(query));
    const filteredUsers = isEmpty(query) ? users : users.filter(u => u.username.includes(query));
    const all: MentionOption[] = [
      ...filteredGroups.map(g => ({ kind: 'group' as const, data: g })),
      ...filteredUsers.map(u => ({ kind: 'user' as const, data: u })),
    ];
    return {
      mentionOptions: all.slice(0, MENTION_ITEM_LIMIT),
      hasMoreMentions: all.length > MENTION_ITEM_LIMIT,
    };
  }, [value, cursorPosition, groups, users]);

  const numUrgentRecipients = MBChatInputUtil.numUrgentRecipients(value, users);

  const handleChangeText = useCallback(
    (newValue: string) => {
      setValue(newValue);
      onChangeText?.(newValue);
    },
    [onChangeText],
  );

  /*
   * When we click on a mention, we replace the current mention word
   * with the full mention.
   * TODO(fant): add unit tests for this logic.
   */
  const onAddMention = useCallback(
    (mention: string): number => {
      const { string, cursorPosition: newCursorPosition } = MBChatInputUtil.addMention(
        value,
        cursorPosition,
        mention,
      );
      setValue(string);
      setCursorPosition(newCursorPosition);
      onChangeText?.(string);
      return newCursorPosition;
    },
    [value, cursorPosition, onChangeText],
  );

  const onPressUser = useCallback(
    (user: API.ShortUserFragment) => {
      MBAnalytics.track('Chat::Input::Mention::PressUser', { id: user.id, draftId });
      const newCursorPosition = onAddMention(user.username);
      inputBar.current?.focus();
      requestAnimationFrame(() => inputBar.current?.setCursorPosition(newCursorPosition));
    },
    [draftId, onAddMention],
  );

  const onPressGroup = useCallback(
    (group: API.ChannelGroupFragment) => {
      MBAnalytics.track('Chat::Input::Mention::PressGroup', { id: group.id, draftId });
      const newCursorPosition = onAddMention(group.id);
      inputBar.current?.focus();
      requestAnimationFrame(() => inputBar.current?.setCursorPosition(newCursorPosition));
    },
    [draftId, onAddMention],
  );

  const onAddEmoji = useCallback(
    (emoji: string) => {
      const before = value.substr(0, cursorPosition);
      const after = value.substr(cursorPosition);
      const newValue = [before, emoji, after].join('');
      handleChangeText(newValue);
      setCursorPosition(cursorPosition + emoji.length);
    },
    [value, cursorPosition, handleChangeText],
  );

  const handleSend = useCallback(() => {
    onSend(value);
    setValue('');
    setCursorPosition(0);
    onChangeText?.('');
  }, [onSend, value, onChangeText]);

  const handleCancelEdit = useCallback(() => {
    setValue('');
    onCancelEdit();
  }, [onCancelEdit]);

  return (
    <View relative paddingHorizontal={paddingHorizontal}>
      <MentionsMenu
        anchorName="--chat-input"
        options={mentionOptions}
        hasMore={hasMoreMentions}
        lightDismiss={!isInputFocused}
        onClickUser={onPressUser}
        onClickGroup={onPressGroup}
      />

      <MBChatInputBar
        innerRef={inputBar}
        draftId={draftId}
        autoFocus={autoFocus}
        isEditing={isEditing ?? false}
        hideUrgent={hideUrgent}
        value={value}
        placeholder={placeholder}
        onSend={handleSend}
        onCancelEdit={handleCancelEdit}
        onBlur={() => {
          setIsInputFocused(false);
          onBlur?.();
        }}
        onFocus={() => {
          setIsInputFocused(true);
          onFocus?.();
        }}
        onChangeText={handleChangeText}
        onSubmitEditing={handleSend}
        onCursorPositionChange={setCursorPosition}
        onAddEmoji={onAddEmoji}
      />

      {numUrgentRecipients > 0 && <MBUrgentBanner draftId={draftId} count={numUrgentRecipients} />}
      <MBChatInputGIFModal draftId={draftId} />
    </View>
  );
}
